import { Form, Button, Alert, Spinner } from 'react-bootstrap';
import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { login, setIsLoading } from '../general/actions';
import axios from 'axios';
import { ensureCsrfCookie } from '../general/axiosInstance';
import logo from '../../images/logo.png';

// Solo se permite volver a rutas internas y nunca a las de auth (evita open-redirect y bucles).
const AUTH_PATHS = ['/login', '/logout', '/register', '/recover-password'];
function safeDestination(from) {
  const path = typeof from === 'string' ? from : (from?.pathname || '');
  if (!path.startsWith('/') || path.startsWith('//')) return '/main';
  if (AUTH_PATHS.some((p) => path === p || path.startsWith(p + '/'))) return '/main';
  return path + (from?.search || '') + (from?.hash || '');
}

function errorMessage(error) {
  const status = error?.response?.status;
  if (status === 401) return 'Usuario o contraseña incorrecto.';
  if (status === 422) return error.response.data?.message || 'Revisa los datos ingresados.';
  if (status === 429) return 'Demasiados intentos. Espera un momento antes de volver a intentar.';
  if (error?.code === 'ERR_NETWORK') return 'No hay conexión con el servidor. Verifica tu internet.';
  return 'No pudimos iniciar sesión. Intenta de nuevo.';
}

function Login({ isLoggedIn, sessionExpired, login, setIsLoading }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const passwordRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const destination = safeDestination(location.state?.from);

  useEffect(() => { setIsLoading(false); }, [setIsLoading]);

  // Única fuente de navegación: cuando hay sesión, ir al destino original (o /main).
  useEffect(() => {
    if (isLoggedIn) navigate(destination, { replace: true });
  }, [isLoggedIn, destination, navigate]);

  const loginOnServer = async (event) => {
    event.preventDefault();
    if (submitting) return;
    setError('');
    setSubmitting(true);
    try {
      await ensureCsrfCookie();
      const response = await axios.post('/login', { email: email.trim(), password });
      login(response.data.user);
    } catch (err) {
      setError(errorMessage(err));
      setPassword('');
      setSubmitting(false);
      passwordRef.current?.focus();
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-logo">
          <img src={logo} height="44" alt="Abi" className="rounded" />
        </div>
        <div className="auth-title">Bienvenido a Abi</div>
        <div className="auth-subtitle">Ingresa tus credenciales para continuar</div>

        {sessionExpired && !error && (
          <Alert variant="warning" className="py-2" style={{ fontSize: 13 }} role="status">
            <i className="fa fa-clock-o" /> Tu sesión expiró. Ingresa de nuevo para continuar donde ibas.
          </Alert>
        )}
        {error && (
          <Alert variant="danger" className="py-2" style={{ fontSize: 13 }} role="alert">
            <i className="fa fa-exclamation-circle" /> {error}
          </Alert>
        )}

        <Form onSubmit={loginOnServer} noValidate={false}>
          <Form.Group className="mb-3">
            <Form.Label className="report-field-label" htmlFor="login-email">Correo electrónico</Form.Label>
            <div className="auth-input-wrap">
              <i className="fa fa-envelope auth-input-icon" />
              <Form.Control
                id="login-email"
                type="email"
                required
                autoFocus
                placeholder="correo@entidad.com"
                autoComplete="username"
                value={email}
                disabled={submitting}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label className="report-field-label" htmlFor="login-password">Contraseña</Form.Label>
            <div className="auth-input-wrap">
              <i className="fa fa-lock auth-input-icon" />
              <Form.Control
                id="login-password"
                ref={passwordRef}
                type={showPassword ? 'text' : 'password'}
                required
                placeholder="••••••••"
                autoComplete="current-password"
                style={{ paddingRight: 38 }}
                value={password}
                disabled={submitting}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                className="auth-input-toggle"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
              >
                <i className={showPassword ? 'fa fa-eye-slash' : 'fa fa-eye'} />
              </button>
            </div>
          </Form.Group>

          <Button
            variant="primary"
            type="submit"
            className="w-100 auth-submit-btn"
            disabled={submitting || !email || !password}
          >
            {submitting
              ? <><Spinner animation="border" size="sm" /> Ingresando…</>
              : <><i className="fa fa-sign-in" /> Ingresar</>}
          </Button>
        </Form>

        <div className="auth-divider">
          <Link to="/recover-password" style={{ color: 'var(--abi-primary)', fontWeight: 600, fontSize: 13 }}>
            ¿Olvidaste tu contraseña?
          </Link>
        </div>

        <div style={{ textAlign: 'center', marginTop: 8 }}>
          <Link to="/home" style={{ color: 'var(--abi-muted)', fontSize: 13 }}>
            ← Regresar al inicio
          </Link>
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({
  isLoggedIn: state.isLoggedIn,
  sessionExpired: state.sessionExpired,
});

export default connect(mapStateToProps, { login, setIsLoading })(Login);
